import axiosInstance from "./axiosInstance";

export interface EarlyStageVerificationStatus {
  status: string;
  ageTier?: string | null;
  priceTier?: string | null;
  canPay: boolean;
  requiresAdminApproval: boolean;
  canSelectPriceTier: boolean;
  rejectedReason?: string | null;
  submittedAt?: string | null;
  verifiedAt?: string | null;
  resubmissionCount: number;
  rejectedAt?: string | null;
  showRejectionNotice?: boolean;
}

export interface EarlyStageResolvedProduct {
  productId: string;
  stripePriceId: string;
  monthlyFeeGbp: number;
  ageTier: string;
  priceTier: string;
  canPay: boolean;
}

export interface EarlyStagePendingUser {
  userId: number;
  objectId?: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth?: string;
  ageTier?: string | null;
  idDocumentSasUrl?: string;
  submittedAt?: string;
  resubmissionCount: number;
}

export async function getEarlyStageStatus(): Promise<EarlyStageVerificationStatus> {
  const { data } = await axiosInstance.get<EarlyStageVerificationStatus>(
    "/EarlyStageMembership/status"
  );
  return data;
}

export async function resolveEarlyStageProduct(): Promise<EarlyStageResolvedProduct> {
  const { data } = await axiosInstance.get<EarlyStageResolvedProduct>(
    "/EarlyStageMembership/resolve-product"
  );
  return data;
}

export async function submitEarlyStageVerification(options: {
  ageTier?: "Under23" | "TwentyThreePlus";
  dateOfBirth?: string;
  idDocument?: File | null;
  phoneNumber?: string;
  city?: string;
  country?: string;
  /** Set when continuing at £10/month in the same step as the under-23 ID submission. */
  proceedAtStandardRate?: boolean;
}): Promise<EarlyStageVerificationStatus> {
  const form = new FormData();
  if (options.ageTier) form.append("AgeTier", options.ageTier);
  if (options.proceedAtStandardRate) {
    form.append("ProceedAtStandardRate", "true");
  }
  if (options.dateOfBirth?.trim()) {
    form.append("DateOfBirth", options.dateOfBirth.trim());
  }
  if (options.phoneNumber?.trim()) {
    form.append("PhoneNumber", options.phoneNumber.trim());
  }
  if (options.city?.trim()) form.append("City", options.city.trim());
  if (options.country?.trim()) form.append("Country", options.country.trim());
  if (options.idDocument) form.append("idDocument", options.idDocument);
  const { data } = await axiosInstance.post<EarlyStageVerificationStatus>(
    "/EarlyStageMembership/submit-verification",
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

/** Continue at £10/month while under-23 ID verification is still pending. */
export async function continueEarlyStageAtStandardRate(): Promise<EarlyStageVerificationStatus> {
  const { data } = await axiosInstance.post<EarlyStageVerificationStatus>(
    "/EarlyStageMembership/continue-standard-rate"
  );
  return data;
}

export async function selectEarlyStagePriceTier(
  priceTier: "Standard2" | "Premium10"
): Promise<EarlyStageVerificationStatus> {
  const { data } = await axiosInstance.post<EarlyStageVerificationStatus>(
    "/EarlyStageMembership/select-price-tier",
    { priceTier }
  );
  return data;
}

export interface EarlyStageRejectionContactResult {
  recipientEmail: string;
}

/** Sends rejection feedback; HTML/text email is built on the API with Connect branding. */
export async function sendEarlyStageRejectionContact(payload: {
  feedback: string;
  subject?: string;
}): Promise<EarlyStageRejectionContactResult> {
  const { data } = await axiosInstance.post<EarlyStageRejectionContactResult>(
    "/EarlyStageMembership/contact-rejection-feedback",
    payload
  );
  return data;
}

export interface EarlyStageAdminApproval {
  userId: number;
  objectId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  city?: string;
  country?: string;
  dateOfBirth?: string;
  ageTier?: string | null;
  priceTier?: string | null;
  verificationStatus: string;
  displayStatus: string;
  statusMeaning: string;
  isActiveQueue: boolean;
  idDocumentSasUrl?: string;
  professionalPhotoSasUrl?: string;
  submittedAt?: string;
  verifiedAt?: string;
  rejectedAt?: string;
  rejectedReason?: string;
  resubmissionCount: number;
  hasPaid: number;
  customerId?: string | null;
  subscriptionId?: string | null;
  stripeStatus?: string | null;
}

export type EarlyStageAdminStatusTarget =
  | "NotSubmitted"
  | "Pending"
  | "UnderReview"
  | "Approved"
  | "Rejected"
  | "Expired";

export async function getEarlyStageAdminApprovals(): Promise<
  EarlyStageAdminApproval[]
> {
  const { data } = await axiosInstance.get<EarlyStageAdminApproval[]>(
    "/EarlyStageMembership/admin/approvals"
  );
  return data;
}

export async function setEarlyStageAdminStatus(
  userId: number,
  payload: { status: EarlyStageAdminStatusTarget; reason?: string }
): Promise<EarlyStageAdminApproval> {
  const { data } = await axiosInstance.put<EarlyStageAdminApproval>(
    `/EarlyStageMembership/admin/set-status/${userId}`,
    payload
  );
  return data;
}

export async function getEarlyStagePendingUsers(): Promise<EarlyStagePendingUser[]> {
  const { data } = await axiosInstance.get<EarlyStagePendingUser[]>(
    "/EarlyStageMembership/admin/pending"
  );
  return data;
}

export async function approveEarlyStageUser(userId: number): Promise<void> {
  await axiosInstance.post(`/EarlyStageMembership/admin/approve/${userId}`);
}

export async function rejectEarlyStageUser(
  userId: number,
  reason: string
): Promise<void> {
  await axiosInstance.post(`/EarlyStageMembership/admin/reject/${userId}`, {
    reason,
  });
}

/** Undo approve/reject and return application to the pending review queue. */
export async function cancelEarlyStageAdminDecision(
  userId: number
): Promise<EarlyStageAdminApproval> {
  const { data } = await axiosInstance.post<EarlyStageAdminApproval>(
    `/EarlyStageMembership/admin/cancel-decision/${userId}`
  );
  return data;
}
